import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Select, TimePicker, message, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import api from '../utils/api';
import moment from 'moment';

const { Option } = Select;

const Schedules = () => {
  const [schedules, setSchedules] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchSchedules();
    fetchDepartments();
  }, []);

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/departments');
      const departmentsWithSchedules = response.data.map(dept => ({
        ...dept,
        working_hours: dept.working_hours || {
          monday: { start: '', end: '' },
          tuesday: { start: '', end: '' },
          wednesday: { start: '', end: '' },
          thursday: { start: '', end: '' },
          friday: { start: '', end: '' },
          saturday: { start: '', end: '' },
          sunday: { start: '', end: '' },
        }
      }));
      setSchedules(departmentsWithSchedules);
    } catch (error) {
      message.error('Failed to fetch schedules');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/api/departments');
      setDepartments(response.data);
    } catch (error) {
      message.error('Failed to fetch departments');
    }
  };

  const handleAddSchedule = () => {
    setModalVisible(true);
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    form.resetFields();
  };

  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();
      const workingHours = {};
      
      ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].forEach(day => {
        if (values[day] && values[day].length === 2) {
          workingHours[day] = {
            start: values[day][0].format('HH:mm'),
            end: values[day][1].format('HH:mm')
          };
        } else {
          workingHours[day] = {
            start: '',
            end: ''
          };
        }
      });

      setLoading(true);
      await api.put(`/api/departments/${values.department_id}/schedule`, {
        working_hours: workingHours
      });

      message.success('Schedule updated successfully');
      setModalVisible(false);
      form.resetFields();
      fetchSchedules();
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to update schedule');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Department',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Monday',
      key: 'monday',
      render: (_, record) => (
        <span>
          {record.working_hours?.monday?.start && record.working_hours?.monday?.end && 
           record.working_hours.monday.start !== '' && record.working_hours.monday.end !== ''
            ? `${record.working_hours.monday.start} - ${record.working_hours.monday.end}`
            : 'Not set'}
        </span>
      ),
    },
    {
      title: 'Tuesday',
      key: 'tuesday',
      render: (_, record) => (
        <span>
          {record.working_hours?.tuesday?.start && record.working_hours?.tuesday?.end && 
           record.working_hours.tuesday.start !== '' && record.working_hours.tuesday.end !== ''
            ? `${record.working_hours.tuesday.start} - ${record.working_hours.tuesday.end}`
            : 'Not set'}
        </span>
      ),
    },
    {
      title: 'Wednesday',
      key: 'wednesday',
      render: (_, record) => (
        <span>
          {record.working_hours?.wednesday?.start && record.working_hours?.wednesday?.end && 
           record.working_hours.wednesday.start !== '' && record.working_hours.wednesday.end !== ''
            ? `${record.working_hours.wednesday.start} - ${record.working_hours.wednesday.end}`
            : 'Not set'}
        </span>
      ),
    },
    {
      title: 'Thursday',
      key: 'thursday',
      render: (_, record) => (
        <span>
          {record.working_hours?.thursday?.start && record.working_hours?.thursday?.end && 
           record.working_hours.thursday.start !== '' && record.working_hours.thursday.end !== ''
            ? `${record.working_hours.thursday.start} - ${record.working_hours.thursday.end}`
            : 'Not set'}
        </span>
      ),
    },
    {
      title: 'Friday',
      key: 'friday',
      render: (_, record) => (
        <span>
          {record.working_hours?.friday?.start && record.working_hours?.friday?.end && 
           record.working_hours.friday.start !== '' && record.working_hours.friday.end !== ''
            ? `${record.working_hours.friday.start} - ${record.working_hours.friday.end}`
            : 'Not set'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => {
            form.setFieldsValue({
              department_id: record.id,
              ...Object.entries(record.working_hours || {}).reduce((acc, [day, times]) => {
                if (times.start && times.end) {
                  acc[day] = [moment(times.start, 'HH:mm'), moment(times.end, 'HH:mm')];
                }
                return acc;
              }, {})
            });
            setModalVisible(true);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Department Schedules</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddSchedule}
        >
          Add Schedule
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={schedules}
        rowKey="id"
        loading={loading}
        scroll={{ x: true }}
      />

      <Modal
        title="Set Department Schedule"
        open={modalVisible}
        onOk={handleModalSubmit}
        onCancel={handleModalCancel}
        width={800}
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Please select department!' }]}
          >
            <Select>
              {departments.map(dept => (
                <Option key={dept.id} value={dept.id}>{dept.name}</Option>
              ))}
            </Select>
          </Form.Item>

          {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
            <Form.Item
              key={day}
              name={day}
              label={day.charAt(0).toUpperCase() + day.slice(1)}
            >
              <TimePicker.RangePicker format="HH:mm" />
            </Form.Item>
          ))}
        </Form>
      </Modal>
    </div>
  );
};

export default Schedules; 